'use client'

import clsx from 'clsx'
import type React from 'react'
import { createContext, useContext, useState } from 'react'

import { Link } from '@/ui-components/Link'

const TableContext = createContext<{ bleed: boolean; dense: boolean; grid: boolean; striped: boolean }>({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
})

export const Table = ({
  bleed = false,
  dense = false,
  grid = false,
  striped = false,
  className,
  children,
  ...props
}: { bleed?: boolean; dense?: boolean; grid?: boolean; striped?: boolean } & React.ComponentPropsWithoutRef<'div'>) => {
  return (
    <TableContext.Provider value={{ bleed, dense, grid, striped } as React.ContextType<typeof TableContext>}>
      <div className="flow-root">
        <div
          {...props}
          className={clsx(className, '-mx-(--gutter) overflow-x-auto whitespace-nowrap')}
        >
          <div className={clsx('inline-block min-w-full align-middle', !bleed && 'sm:px-(--gutter)')}>
            <table className="min-w-full text-left text-sm/6 text-gray-900 dark:text-white">{children}</table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  )
}

export const TableHead = ({ className, ...props }: React.ComponentPropsWithoutRef<'thead'>) => {
  return (
    <thead
      {...props}
      className={clsx(className, 'text-gray-500 dark:text-gray-400')}
    />
  )
}

export const TableBody = (props: React.ComponentPropsWithoutRef<'tbody'>) => {
  return <tbody {...props} />
}

const TableRowContext = createContext<{ to?: string; target?: string; title?: string }>({
  to: undefined,
  target: undefined,
  title: undefined,
})

export const TableRow = ({
  to,
  target,
  title,
  className,
  ...props
}: { to?: string; target?: string; title?: string } & React.ComponentPropsWithoutRef<'tr'>) => {
  const { striped } = useContext(TableContext)
  return (
    <TableRowContext.Provider value={{ to, target, title } as React.ContextType<typeof TableRowContext>}>
      <tr
        {...props}
        className={clsx(
          className,
          to &&
            'has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-primary-500 dark:focus-within:bg-white/[2.5%]',
          striped && 'even:bg-gray-900/[2.5%] dark:even:bg-white/[2.5%]',
          to && striped && 'hover:bg-gray-900/5 dark:hover:bg-white/5',
          to && !striped && 'hover:bg-gray-900/[2.5%] dark:hover:bg-white/[2.5%]',
        )}
      />
    </TableRowContext.Provider>
  )
}

export const TableHeader = ({ className, ...props }: React.ComponentPropsWithoutRef<'th'>) => {
  const { bleed, grid } = useContext(TableContext)
  return (
    <th
      {...props}
      className={clsx(
        className,
        'border-b border-b-gray-900/10 px-4 py-2 font-medium first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2)) dark:border-b-white/10',
        grid && 'border-l border-l-gray-900/5 first:border-l-0 dark:border-l-white/5',
        !bleed && 'sm:first:pl-1 sm:last:pr-1',
      )}
    />
  )
}

export const TableCell = ({ className, children, ...props }: React.ComponentPropsWithoutRef<'td'>) => {
  const { bleed, dense, grid, striped } = useContext(TableContext)
  const { to, target, title } = useContext(TableRowContext)
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null)

  return (
    <td
      ref={to ? setCellRef : undefined}
      {...props}
      className={clsx(
        className,
        'relative px-4 first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2))',
        !striped && 'border-b border-gray-900/5 dark:border-white/5',
        grid && 'border-l border-l-gray-900/5 first:border-l-0 dark:border-l-white/5',
        dense ? 'py-2.5' : 'py-4',
        !bleed && 'sm:first:pl-1 sm:last:pr-1',
      )}
    >
      {to && (
        <Link
          data-row-link
          to={to}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-hidden"
        />
      )}
      {children}
    </td>
  )
}
